import { useCallback, useEffect, useState } from "react";
import { TableMap } from "@tiptap/pm/tables";
import {
  Table as TableIcon, ChevronDown, Trash2, Rows3, Columns3, Grid2x2,
  SquareSplitHorizontal, Merge, PanelTop, PanelLeft, Paintbrush, Square,
  AlignLeft, AlignCenter, AlignRight, Bookmark, Save, Star, ClipboardCopy, Wand2, Eraser,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TABLE_STYLE_KEYS } from "@/components/kbTableExtensions";

const GRID_ROWS = 8;
const GRID_COLS = 10;

const SWATCHES = [
  "#ffffff", "#f8fafc", "#f1f5f9", "#e2e8f0", "#cbd5e1", "#94a3b8",
  "#475569", "#1e293b", "#dcfce7", "#65a30d", "#fef3c7", "#fecaca",
  "#dbeafe", "#e9d5ff",
];
const SCOPES = [
  { id: "table", label: "Table", icon: Grid2x2 },
  { id: "row", label: "Row", icon: Rows3 },
  { id: "column", label: "Column", icon: Columns3 },
  { id: "cell", label: "Cell", icon: Square },
];
const BORDER_WIDTHS = ["0px", "1px", "2px", "3px", "4px"];
const BORDER_STYLES = ["solid", "dashed", "dotted", "double", "none"];
const PADDINGS = [
  { label: "Tight", value: "2px 4px" },
  { label: "Compact", value: "4px 8px" },
  { label: "Normal", value: "8px 11px" },
  { label: "Roomy", value: "12px 16px" },
  { label: "Airy", value: "18px 22px" },
];
const V_ALIGNS = ["top", "middle", "bottom"];
const PRESET_KEYS = [
  "borderColor", "borderWidth", "borderStyle", "backgroundColor",
  "headerBackgroundColor", "padding", "textAlign", "verticalAlign",
];

function nodeAtRole($pos, roles) {
  for (let d = $pos.depth; d > 0; d--) {
    const node = $pos.node(d);
    if (roles.includes(node.type.spec.tableRole)) return { node, pos: $pos.before(d) };
  }
  return null;
}

function tableContext(editor) {
  const sel = editor.state.selection;
  const table = nodeAtRole(sel.$from, ["table"]);
  const cell = nodeAtRole(sel.$from, ["cell", "header_cell"]);
  if (!table || !cell) return null;
  return { sel, table, cell, map: TableMap.get(table.node), start: table.pos + 1 };
}

/** Apply border / shading / spacing attributes to every cell in the chosen scope. */
export function applyTableStyle(editor, scope, attrs) {
  const ctx = tableContext(editor);
  if (!ctx) return;
  const { sel, table, cell, map, start } = ctx;
  const rect = map.findCell(cell.pos - start);
  let offsets;
  if (scope === "cell" && typeof sel.forEachCell === "function") {
    offsets = [];
    sel.forEachCell((_n, pos) => offsets.push(pos - start));
  } else if (scope === "row") {
    offsets = map.cellsInRect({ left: 0, right: map.width, top: rect.top, bottom: rect.bottom });
  } else if (scope === "column") {
    offsets = map.cellsInRect({ left: rect.left, right: rect.right, top: 0, bottom: map.height });
  } else if (scope === "table") {
    offsets = map.cellsInRect({ left: 0, right: map.width, top: 0, bottom: map.height });
  } else {
    offsets = [cell.pos - start];
  }
  const tr = editor.state.tr;
  offsets.forEach((off) => {
    const pos = start + off;
    const node = tr.doc.nodeAt(pos);
    if (node) tr.setNodeMarkup(pos, null, { ...node.attrs, ...attrs });
  });
  if (scope === "table") {
    const tnode = tr.doc.nodeAt(table.pos);
    if (tnode) tr.setNodeMarkup(table.pos, null, { ...tnode.attrs, ...attrs });
  }
  if (tr.docChanged) editor.view.dispatch(tr);
  editor.commands.focus();
}

/** Apply a saved preset to the whole table — header cells get their own shade. */
export function applyPreset(editor, style) {
  const ctx = tableContext(editor);
  if (!ctx) return;
  const { table, map, start } = ctx;
  const base = {};
  ["borderColor", "borderWidth", "borderStyle", "padding", "textAlign", "verticalAlign"]
    .forEach((k) => { if (style[k]) base[k] = style[k]; });
  const tr = editor.state.tr;
  map.cellsInRect({ left: 0, right: map.width, top: 0, bottom: map.height }).forEach((off) => {
    const pos = start + off;
    const node = tr.doc.nodeAt(pos);
    if (!node) return;
    const isHeader = node.type.spec.tableRole === "header_cell";
    const bg = isHeader
      ? (style.headerBackgroundColor || style.backgroundColor || null)
      : (style.backgroundColor || null);
    tr.setNodeMarkup(pos, null, { ...node.attrs, ...base, backgroundColor: bg });
  });
  const tnode = tr.doc.nodeAt(table.pos);
  if (tnode) tr.setNodeMarkup(table.pos, null, { ...tnode.attrs, ...base });
  if (tr.docChanged) editor.view.dispatch(tr);
  editor.commands.focus();
}

/** One click: take Helpjuice's cell styling off this table, make row one a real
 *  header row, then lay our house preset over it. A single transaction, so
 *  Ctrl-Z puts the imported table straight back. */
export function applyHouseStyle(editor, style) {
  const first = tableContext(editor);
  if (!first) return false;
  // Imported tables use <td> in the head row; a real header row is what makes
  // the house style land on the right cells.
  const headRow = first.map.cellsInRect({ left: 0, right: first.map.width, top: 0, bottom: 1 });
  const hasHeader = headRow.some((off) => {
    const n = editor.state.doc.nodeAt(first.start + off);
    return n?.type.spec.tableRole === "header_cell";
  });
  if (!hasHeader) editor.chain().focus().toggleHeaderRow().run();

  const ctx = tableContext(editor);
  if (!ctx) return false;
  const { table, map, start } = ctx;
  const base = {};
  ["borderColor", "borderWidth", "borderStyle", "padding", "textAlign", "verticalAlign"]
    .forEach((k) => { base[k] = style?.[k] || null; });
  const tr = editor.state.tr;
  map.cellsInRect({ left: 0, right: map.width, top: 0, bottom: map.height }).forEach((off) => {
    const pos = start + off;
    const node = tr.doc.nodeAt(pos);
    if (!node) return;
    const isHeader = node.type.spec.tableRole === "header_cell";
    tr.setNodeMarkup(pos, null, {
      ...node.attrs, ...base,
      colwidth: null,
      backgroundColor: isHeader
        ? (style?.headerBackgroundColor || style?.backgroundColor || null)
        : (style?.backgroundColor || null),
    });
  });
  const tnode = tr.doc.nodeAt(table.pos);
  if (tnode) tr.setNodeMarkup(table.pos, null, { ...tnode.attrs, ...base });
  if (tr.docChanged) editor.view.dispatch(tr);
  editor.commands.focus();
  return true;
}

/** Attributes of the table the cursor is in. */
const tableAttrs = (editor) => {
  const { $head } = editor.state.selection;
  for (let d = $head.depth; d > 0; d -= 1) {
    if ($head.node(d).type.name === "table") return $head.node(d).attrs;
  }
  return {};
};

/** Strip every bit of styling an author (or an import, or our own default) put
 *  on this table — inline borders, shading, padding, alignment and the column
 *  widths that come with dragging a border. One transaction, so Ctrl-Z brings
 *  the old look back. */
export function clearTableStyling(editor, { plain } = {}) {
  const ctx = tableContext(editor);
  if (!ctx) return false;
  const { table, map, start } = ctx;
  const blank = {};
  TABLE_STYLE_KEYS.forEach((k) => { blank[k] = null; });
  const tr = editor.state.tr;
  map.cellsInRect({ left: 0, right: map.width, top: 0, bottom: map.height }).forEach((off) => {
    const pos = start + off;
    const node = tr.doc.nodeAt(pos);
    if (node) {
      tr.setNodeMarkup(pos, null, {
        ...node.attrs, ...blank, colwidth: null,
        plain: plain === undefined ? node.attrs.plain : plain,
      });
    }
  });
  const tnode = tr.doc.nodeAt(table.pos);
  if (tnode) {
    tr.setNodeMarkup(table.pos, null, {
      ...tnode.attrs, ...blank,
      plain: plain === undefined ? tnode.attrs.plain : plain,
    });
  }
  if (tr.docChanged) editor.view.dispatch(tr);
  editor.commands.focus();
  return true;
}

/** The look of the cell the cursor sits in — seeds the live preview. */
export function readCurrentCellStyle(editor) {
  const ctx = tableContext(editor);
  if (!ctx) return {};
  const out = {};
  ["borderColor", "borderWidth", "borderStyle", "backgroundColor",
    "padding", "textAlign", "verticalAlign"].forEach((k) => {
    if (ctx.cell.node.attrs[k]) out[k] = ctx.cell.node.attrs[k];
  });
  return out;
}

/** Read the current table's look so it can be saved as a preset. */
export function readTableStyle(editor) {
  const ctx = tableContext(editor);
  if (!ctx) return null;
  const { table, map, start } = ctx;
  const offsets = map.cellsInRect({ left: 0, right: map.width, top: 0, bottom: map.height });
  let header = null; let body = null;
  offsets.forEach((off) => {
    const node = editor.state.doc.nodeAt(start + off);
    if (!node) return;
    if (node.type.spec.tableRole === "header_cell") header = header || node;
    else body = body || node;
  });
  const src = body || header || null;
  if (!src) return null;
  const style = {};
  PRESET_KEYS.forEach((k) => {
    if (k === "headerBackgroundColor") return;
    if (src.attrs[k]) style[k] = src.attrs[k];
  });
  if (header?.attrs.backgroundColor) style.headerBackgroundColor = header.attrs.backgroundColor;
  if (!Object.keys(style).length && table.node.attrs.borderColor) {
    style.borderColor = table.node.attrs.borderColor;
  }
  return style;
}

/** Iter 120 — mini 3×3 preview so a colour/border/padding choice can be
 *  judged before it lands on a 20-row table. Cells covered by the current
 *  scope are the ones that show the pending look, which doubles as a visual
 *  explanation of what "Row" / "Column" / "Cell" will touch. */
function StylePreview({ scope, style }) {
  const inScope = (r, c) => (
    scope === "table" ? true
      : scope === "row" ? r === 1
        : scope === "column" ? c === 1
          : r === 1 && c === 1
  );
  const base = {
    borderStyle: style.borderStyle || "solid",
    borderWidth: style.borderWidth || "1px",
    borderColor: style.borderColor || "#cbd5e1",
    padding: style.padding || "6px 8px",
    textAlign: style.textAlign || "left",
    verticalAlign: style.verticalAlign || "middle",
  };
  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 p-2" data-testid="kb-table-style-preview">
      <table className="w-full border-collapse text-[10px] leading-tight">
        <tbody>
          {[0, 1, 2].map((r) => (
            <tr key={r}>
              {[0, 1, 2].map((c) => {
                const on = inScope(r, c);
                return (
                  <td key={c} style={on
                    ? { ...base, backgroundColor: style.backgroundColor || "transparent" }
                    : { border: "1px dashed #e2e8f0", padding: base.padding, color: "#cbd5e1" }}>
                    <span className={on ? "text-slate-500 dark:text-slate-400" : "text-slate-300 dark:text-slate-600"}>Aa</span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Swatches({ value, onPick, onHover, testid }) {
  return (
    <div className="flex flex-wrap gap-1" onMouseLeave={() => onHover?.(null)}>
      {SWATCHES.map((c) => (
        <button key={c} type="button" title={c} data-testid={`${testid}-${c.replace("#", "")}`}
          onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(c)}
          onMouseEnter={() => onHover?.(c)}
          className={`h-5 w-5 rounded border transition-transform hover:scale-110
            ${value === c ? "border-primary ring-2 ring-primary/30" : "border-slate-300 dark:border-slate-700"}`}
          style={{ background: c }} />
      ))}
      <label className="flex h-5 w-5 cursor-pointer items-center justify-center rounded border border-dashed border-slate-400"
        title="Custom colour" data-testid={`${testid}-custom`}>
        <Paintbrush className="h-3 w-3 text-slate-500 dark:text-slate-400" />
        <input type="color" className="sr-only" onInput={(e) => onPick(e.target.value)} />
      </label>
    </div>
  );
}

const MiniBtn = ({ onClick, children, testid, disabled, className = "" }) => (
  <button type="button" data-testid={testid} disabled={disabled}
    onMouseDown={(e) => e.preventDefault()} onClick={onClick}
    className={`flex items-center gap-1.5 rounded-md border border-slate-200 dark:border-slate-800 px-2 py-1 text-xs text-slate-700 dark:text-slate-200
      transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 ${className}`}>
    {children}
  </button>
);

const Field = ({ label, children }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</span>
    {children}
  </div>
);

/** Insert-table button with a hover grid + manual size entry. */
export function TableGridPicker({ editor }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState({ r: 0, c: 0 });
  const [header, setHeader] = useState(true);
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [defaultStyle, setDefaultStyle] = useState(null);
  // Iter 179 — Keith didn't want the saved default landing on every table he
  // draws, so the choice is his and it sticks.
  const [useHouse, setUseHouse] = useState(
    () => localStorage.getItem("kbTableUseHouseStyle") !== "0");

  useEffect(() => {
    api.get("/kb/table-presets")
      .then(({ data }) => {
        const def = (data.presets || []).find((p) => p.is_default);
        setDefaultStyle(def?.style || null);
      })
      .catch(() => {});
  }, []);

  const insert = (r, c) => {
    editor.chain().focus()
      .insertTable({ rows: Math.max(1, r), cols: Math.max(1, c), withHeaderRow: header })
      .run();
    if (defaultStyle && useHouse) applyPreset(editor, defaultStyle);
    setOpen(false);
    setHover({ r: 0, c: 0 });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" title="Insert table" data-testid="kb-rte-insert-table"
          onMouseDown={(e) => e.preventDefault()}
          className="flex h-8 items-center gap-0.5 rounded-md px-1.5 text-slate-600 dark:text-slate-300 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800">
          <TableIcon className="h-4 w-4" /><ChevronDown className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="bottom" sideOffset={6} collisionPadding={12}
        className="w-auto overflow-y-auto p-3" data-testid="kb-table-grid-popover"
        style={{ maxHeight: "min(70vh, var(--radix-popover-content-available-height, 70vh))" }}>
        <div className="grid gap-[3px]" style={{ gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)` }}
          onMouseLeave={() => setHover({ r: 0, c: 0 })}>
          {Array.from({ length: GRID_ROWS * GRID_COLS }).map((_, i) => {
            const r = Math.floor(i / GRID_COLS) + 1;
            const c = (i % GRID_COLS) + 1;
            const on = r <= hover.r && c <= hover.c;
            return (
              <button key={i} type="button" data-testid={`kb-table-grid-${r}x${c}`}
                onMouseEnter={() => setHover({ r, c })}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => insert(r, c)}
                className={`h-4 w-4 rounded-[3px] border transition-colors
                  ${on ? "border-primary bg-primary/30" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700"}`} />
            );
          })}
        </div>
        <p className="mt-2 text-center text-xs font-medium text-slate-600 dark:text-slate-300" data-testid="kb-table-grid-label">
          {hover.r || rows} × {hover.c || cols}
        </p>
        <div className="mt-3 space-y-2 border-t border-slate-100 dark:border-slate-800 pt-3">
          <div className="flex items-center gap-2">
            <input type="number" min="1" max="100" value={rows} data-testid="kb-table-rows-input"
              onChange={(e) => setRows(Number(e.target.value))}
              className="h-7 w-16 rounded-md border border-slate-200 dark:border-slate-800 px-2 text-xs" placeholder="Rows" />
            <span className="text-xs text-slate-400 dark:text-slate-500">×</span>
            <input type="number" min="1" max="50" value={cols} data-testid="kb-table-cols-input"
              onChange={(e) => setCols(Number(e.target.value))}
              className="h-7 w-16 rounded-md border border-slate-200 dark:border-slate-800 px-2 text-xs" placeholder="Cols" />
            <MiniBtn testid="kb-table-insert-manual" onClick={() => insert(rows, cols)}>Insert</MiniBtn>
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600 dark:text-slate-300" data-testid="kb-table-header-toggle">
            <input type="checkbox" checked={header} onChange={(e) => setHeader(e.target.checked)} />
            Header row
          </label>
          {defaultStyle && (
            <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600 dark:text-slate-300" data-testid="kb-table-house-toggle">
              <input type="checkbox" checked={useHouse}
                onChange={(e) => {
                  setUseHouse(e.target.checked);
                  localStorage.setItem("kbTableUseHouseStyle", e.target.checked ? "1" : "0");
                }} />
              Apply the saved default style
            </label>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Borders / shading / spacing / structure / presets — shown when the cursor is in a table. */
export function TableToolsMenu({ editor }) {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState("cell");
  const [tab, setTab] = useState("style");
  const [borderColor, setBorderColor] = useState("#cbd5e1");
  const [presets, setPresets] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [copyQuery, setCopyQuery] = useState("");
  const [copyHits, setCopyHits] = useState([]);
  const [applied, setApplied] = useState({});
  const [hoverStyle, setHoverStyle] = useState(null);
  const preview = { ...applied, ...(hoverStyle || {}) };

  // Seed the preview from the cell the cursor is in each time the panel opens.
  useEffect(() => {
    if (open) { setApplied(readCurrentCellStyle(editor)); setHoverStyle(null); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Iter 120 — "Copy look from an article": grab the styling of another
  // article's first table without having to save a preset first.
  useEffect(() => {
    const term = copyQuery.trim();
    if (term.length < 2) { setCopyHits([]); return; }
    const t = setTimeout(() => {
      api.get(`/kb/search?q=${encodeURIComponent(term)}&limit=5`)
        .then(({ data }) => setCopyHits(data.results || []))
        .catch(() => setCopyHits([]));
    }, 300);
    return () => clearTimeout(t);
  }, [copyQuery]);

  const copyLookFrom = async (slug) => {
    try {
      const { data } = await api.get(`/kb/articles/${slug}/table-style`);
      applyPreset(editor, data.style || {});
      toast.success(`Copied the look from “${data.name}”`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't copy that look");
    }
  };

  const loadPresets = useCallback(() => {
    api.get("/kb/table-presets")
      .then(({ data }) => { setPresets(data.presets || []); setCanEdit(!!data.can_edit); })
      .catch(() => {});
  }, []);

  useEffect(() => { if (open) loadPresets(); }, [open, loadPresets]);

  const style = (attrs) => {
    applyTableStyle(editor, scope, attrs);
    setApplied((prev) => ({ ...prev, ...attrs }));
    setHoverStyle(null);
  };
  const chain = () => editor.chain().focus();

  const savePreset = async () => {
    const current = readTableStyle(editor);
    if (!current || !Object.keys(current).length) {
      toast.error("Style the table first, then save it as a preset");
      return;
    }
    const name = presetName.trim();
    if (!name) { toast.error("Give the preset a name"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/kb/table-presets", { name, style: current });
      setPresets((p) => [...p, data]);
      setPresetName("");
      toast.success(`Saved "${data.name}"`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save preset");
    } finally { setSaving(false); }
  };

  const makeDefault = async (p) => {
    try {
      await api.post(`/kb/table-presets/${p.preset_id}/default`);
      setPresets((list) => list.map((x) => ({ ...x, is_default: x.preset_id === p.preset_id })));
      toast.success(`New tables will start on "${p.name}"`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't set the default");
    }
  };

  const removePreset = async (p) => {    try {
      await api.delete(`/kb/table-presets/${p.preset_id}`);
      setPresets((list) => list.filter((x) => x.preset_id !== p.preset_id));
      toast.success(`Deleted "${p.name}"`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete preset");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" title="Table properties" data-testid="kb-rte-table-props"
          onMouseDown={(e) => e.preventDefault()}
          className="flex h-8 items-center gap-1 rounded-md bg-primary/10 px-2 text-xs font-medium text-primary">
          <Paintbrush className="h-3.5 w-3.5" /> Table<ChevronDown className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="bottom" sideOffset={6} collisionPadding={12}
        data-testid="kb-table-props-popover"
        className="w-80 space-y-3 overflow-y-auto overscroll-contain p-3"
        style={{ maxHeight: "min(70vh, var(--radix-popover-content-available-height, 70vh))" }}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}>
        <div className="flex gap-1 border-b border-slate-100 dark:border-slate-800 pb-2">
          {[["style", "Style"], ["structure", "Structure"], ["presets", "Presets"]].map(([id, label]) => (
            <button key={id} type="button" data-testid={`kb-table-tab-${id}`}
              onMouseDown={(e) => e.preventDefault()} onClick={() => setTab(id)}
              className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors
                ${tab === id ? "bg-primary/10 text-primary" : "text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "style" && (
        <div>
          <MiniBtn testid="kb-table-house-style" className="w-full justify-center border-primary/40 bg-primary/5 py-1.5 font-medium text-primary hover:bg-primary/10"
            onClick={() => {
              const house = presets.find((p) => p.is_default)
                || presets.find((p) => /house/i.test(p.name));
              if (!house) { toast.error("Save a preset first — then it can be the house style"); return; }
              if (applyHouseStyle(editor, house.style || {})) {
                setApplied(readCurrentCellStyle(editor));
                toast.success(`This table now wears “${house.name}”`);
              }
            }}>
            <Wand2 className="h-3.5 w-3.5" /> Apply house style to this table
          </MiniBtn>
          <div className="mt-1.5 flex gap-1.5">
            <MiniBtn testid="kb-table-clear-styling" className="flex-1 justify-center"
              onClick={() => {
                if (clearTableStyling(editor)) {
                  setApplied(readCurrentCellStyle(editor));
                  toast.success("Styling cleared — Ctrl-Z to bring it back");
                }
              }}>
              <Eraser className="h-3.5 w-3.5" /> Clear styling
            </MiniBtn>
            <MiniBtn testid="kb-table-plain"
              active={!!tableAttrs(editor).plain}
              className="flex-1 justify-center"
              onClick={() => {
                const next = !tableAttrs(editor).plain;
                clearTableStyling(editor, { plain: next || null });
                setApplied(readCurrentCellStyle(editor));
                toast.success(next ? "Plain table — no borders or shading"
                  : "House look restored");
              }}>
              <Square className="h-3.5 w-3.5" /> Plain
            </MiniBtn>
          </div>
          <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
            Clear styling drops the borders, shading and column widths. Plain also switches off the
            house look for this table, for a layout grid that shouldn&apos;t read as data.
          </p>
        </div>
        )}

        {tab === "style" && (
        <div>
          <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Apply to</p>
          <div className="grid grid-cols-4 gap-1">
            {SCOPES.map((s) => (
              <button key={s.id} type="button" data-testid={`kb-table-scope-${s.id}`}
                onMouseDown={(e) => e.preventDefault()} onClick={() => setScope(s.id)}
                className={`flex flex-col items-center gap-1 rounded-md border px-1 py-1.5 text-[11px] transition-colors
                  ${scope === s.id ? "border-primary bg-primary/10 text-primary" : "border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                <s.icon className="h-3.5 w-3.5" />{s.label}
              </button>
            ))}
          </div>
        </div>
        )}

        {tab === "style" && (
        <div className="space-y-2 border-t border-slate-100 dark:border-slate-800 pt-3">
          <StylePreview scope={scope} style={preview} />
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Borders</p>
          <Swatches value={borderColor} testid="kb-table-border-color"
            onHover={(c) => setHoverStyle(c ? { borderColor: c, borderStyle: "solid" } : null)}
            onPick={(c) => { setBorderColor(c); style({ borderColor: c, borderStyle: "solid" }); }} />
          <Field label="Width">
            <select data-testid="kb-table-border-width" defaultValue=""
              onChange={(e) => style(e.target.value === "0px"
                ? { borderStyle: "none", borderWidth: "0px" }
                : { borderWidth: e.target.value, borderStyle: "solid", borderColor })}
              className="h-7 rounded-md border border-slate-200 dark:border-slate-800 px-1.5 text-xs">
              <option value="" disabled>Width…</option>
              {BORDER_WIDTHS.map((w) => <option key={w} value={w}>{w === "0px" ? "None" : w}</option>)}
            </select>
          </Field>
          <Field label="Style">
            <select data-testid="kb-table-border-style" defaultValue=""
              onChange={(e) => style({ borderStyle: e.target.value, borderColor })}
              className="h-7 rounded-md border border-slate-200 dark:border-slate-800 px-1.5 text-xs">
              <option value="" disabled>Style…</option>
              {BORDER_STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </div>
        )}

        {tab === "style" && (
        <div className="space-y-2 border-t border-slate-100 dark:border-slate-800 pt-3">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Shading</p>
            <button type="button" data-testid="kb-table-shading-clear"
              onMouseDown={(e) => e.preventDefault()} onClick={() => style({ backgroundColor: null })}
              className="text-[11px] font-medium text-slate-500 dark:text-slate-400 hover:text-red-500">Clear</button>
          </div>
          <Swatches testid="kb-table-shading"
            onHover={(c) => setHoverStyle(c ? { backgroundColor: c } : null)}
            onPick={(c) => style({ backgroundColor: c })} />
        </div>
        )}

        {tab === "style" && (
        <div className="space-y-2 border-t border-slate-100 dark:border-slate-800 pt-3">
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Spacing &amp; alignment</p>
          <Field label="Padding">
            <select data-testid="kb-table-padding" defaultValue=""
              onChange={(e) => style({ padding: e.target.value || null })}
              className="h-7 rounded-md border border-slate-200 dark:border-slate-800 px-1.5 text-xs">
              <option value="" disabled>Padding…</option>
              {PADDINGS.map((p) => <option key={p.value} value={p.value}>{`${p.label} (${p.value})`}</option>)}
            </select>
          </Field>
          <Field label="Align">
            <div className="flex gap-1">
              {[["left", AlignLeft], ["center", AlignCenter], ["right", AlignRight]].map(([a, Icon]) => (
                <button key={a} type="button" title={`Align ${a}`} data-testid={`kb-table-align-${a}`}
                  onMouseDown={(e) => e.preventDefault()} onClick={() => style({ textAlign: a })}
                  className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
                  <Icon className="h-3.5 w-3.5" />
                </button>
              ))}
            </div>
          </Field>
          <Field label="Vertical">
            <select data-testid="kb-table-valign" defaultValue=""
              onChange={(e) => style({ verticalAlign: e.target.value || null })}
              className="h-7 rounded-md border border-slate-200 dark:border-slate-800 px-1.5 text-xs">
              <option value="" disabled>Vertical…</option>
              {V_ALIGNS.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
        </div>
        )}

        {tab === "presets" && (
        <div className="space-y-1.5">
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Presets</p>
          {presets.length === 0 && (
            <p className="text-[11px] text-slate-400 dark:text-slate-500" data-testid="kb-table-presets-empty">
              No saved looks yet — style a table, then save it below.
            </p>
          )}
          {presets.map((p) => (
            <div key={p.preset_id} className="flex items-center gap-1">
              <MiniBtn testid={`kb-table-preset-apply-${p.preset_id}`} className="flex-1"
                onClick={() => { applyPreset(editor, p.style || {}); toast.success(`Applied "${p.name}"`); }}>
                <Bookmark className="h-3.5 w-3.5 text-primary" />
                <span className="truncate">{p.name}</span>
                {p.is_default && <span className="ml-auto text-[10px] font-medium text-primary">default</span>}
              </MiniBtn>
              {canEdit && (
                <button type="button" data-testid={`kb-table-preset-default-${p.preset_id}`}
                  title={p.is_default ? "Default for new tables" : "Make default for new tables"}
                  onMouseDown={(e) => e.preventDefault()} onClick={() => makeDefault(p)}
                  className={`flex h-7 w-7 items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800
                    ${p.is_default ? "text-amber-500" : "text-slate-300 dark:text-slate-600 hover:text-amber-500"}`}>
                  <Star className={`h-3.5 w-3.5 ${p.is_default ? "fill-amber-400" : ""}`} />
                </button>
              )}
              {canEdit && (
                <button type="button" title="Delete preset" data-testid={`kb-table-preset-delete-${p.preset_id}`}
                  onMouseDown={(e) => e.preventDefault()} onClick={() => removePreset(p)}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-slate-400 dark:text-slate-500 hover:bg-red-50 hover:text-red-500">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
          {canEdit && (
            <div className="flex items-center gap-1 pt-1">
              <input value={presetName} onChange={(e) => setPresetName(e.target.value)}
                data-testid="kb-table-preset-name" placeholder="Name this look"
                className="h-7 flex-1 rounded-md border border-slate-200 dark:border-slate-800 px-2 text-xs" />
              <MiniBtn testid="kb-table-preset-save" disabled={saving} onClick={savePreset}>
                <Save className="h-3.5 w-3.5" /> Save
              </MiniBtn>
            </div>
          )}
        </div>
        )}

        {tab === "presets" && (
        <div className="space-y-1.5 border-t border-slate-100 dark:border-slate-800 pt-3">
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Copy look from an article</p>
          <input value={copyQuery} data-testid="kb-table-copy-search"
            onChange={(e) => setCopyQuery(e.target.value)}
            placeholder="Search articles…"
            className="h-7 w-full rounded-md border border-slate-200 dark:border-slate-800 px-2 text-xs" />
          {copyHits.map((h) => (
            <MiniBtn key={h.slug} testid={`kb-table-copy-${h.slug}`} className="w-full"
              onClick={() => copyLookFrom(h.slug)}>
              <ClipboardCopy className="h-3.5 w-3.5 text-primary" />
              <span className="truncate">{h.name}</span>
            </MiniBtn>
          ))}
          {copyQuery.trim().length > 1 && !copyHits.length && (
            <p className="text-[11px] text-slate-400 dark:text-slate-500" data-testid="kb-table-copy-empty">No matching articles</p>
          )}
        </div>
        )}

        {tab === "structure" && (
        <div className="space-y-1.5">
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Structure</p>
          <div className="grid grid-cols-2 gap-1.5">
            <MiniBtn testid="kb-table-row-before" onClick={() => chain().addRowBefore().run()}><Rows3 className="h-3.5 w-3.5" /> Row above</MiniBtn>
            <MiniBtn testid="kb-table-row-after" onClick={() => chain().addRowAfter().run()}><Rows3 className="h-3.5 w-3.5" /> Row below</MiniBtn>
            <MiniBtn testid="kb-table-col-before" onClick={() => chain().addColumnBefore().run()}><Columns3 className="h-3.5 w-3.5" /> Col left</MiniBtn>
            <MiniBtn testid="kb-table-col-after" onClick={() => chain().addColumnAfter().run()}><Columns3 className="h-3.5 w-3.5" /> Col right</MiniBtn>
            <MiniBtn testid="kb-table-del-row" onClick={() => chain().deleteRow().run()}><Trash2 className="h-3.5 w-3.5" /> Delete row</MiniBtn>
            <MiniBtn testid="kb-table-del-col" onClick={() => chain().deleteColumn().run()}><Trash2 className="h-3.5 w-3.5" /> Delete col</MiniBtn>
            <MiniBtn testid="kb-table-header-row" onClick={() => chain().toggleHeaderRow().run()}><PanelTop className="h-3.5 w-3.5" /> Header row</MiniBtn>
            <MiniBtn testid="kb-table-header-col" onClick={() => chain().toggleHeaderColumn().run()}><PanelLeft className="h-3.5 w-3.5" /> Header col</MiniBtn>
            <MiniBtn testid="kb-table-merge" onClick={() => chain().mergeCells().run()}><Merge className="h-3.5 w-3.5" /> Merge</MiniBtn>
            <MiniBtn testid="kb-table-split" onClick={() => chain().splitCell().run()}><SquareSplitHorizontal className="h-3.5 w-3.5" /> Split</MiniBtn>
          </div>
          <MiniBtn testid="kb-table-delete" onClick={() => { chain().deleteTable().run(); setOpen(false); }}>
            <Trash2 className="h-3.5 w-3.5 text-red-500" /> Delete table
          </MiniBtn>
        </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
